import { useCallback } from 'react';

import { Companies } from '@/api/Companies';
import { Organization } from '@/api/Organization';
import { Organizations } from '@/api/Organizations';
import { RebatePolicies } from '@/api/RebatePolicies';
import { SignupRequests } from '@/api/SignupRequests';
import {
  CompanyRequest,
  CompanyRequestDataWithFiles,
  CompanyRequestFiles,
  CompanyResponse,
  DiscountAndPayment,
  OrganizationDetailsResponse,
  OrganizationResponse,
  OrganizationSettings,
  OrganizationUpdateName,
  OrganizationUpdateRequestDataWithFiles,
  OrganizationUpdateTenants,
  PageResponseCompanyResponse,
  PageResponseRebatePolicyResponse,
  RebatePoliciesRequest,
  RebatePolicyRequest,
  RebatePolicyResponse,
} from '@/api/data-contracts';
import { ContentType } from '@/api/http-client';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

const useCompaniesService = () => {
  const companiesApi = useUploadProgressService(Companies);
  const organizationApi = useUploadProgressService(Organization);
  const organizationsApi = useUploadProgressService(Organizations);
  const rebatePolicyApi = useUploadProgressService(RebatePolicies);
  const signupRequestsApi = useUploadProgressService(SignupRequests);

  const getCompanies = async (
    query: {
      request: CompanyRequest;
    },
    signal: AbortSignal | undefined,
  ): Promise<PageResponseCompanyResponse> => {
    const { data } = await companiesApi.allCompanies(query.request, {
      signal,
    });
    return data;
  };

  const createCompany = async (form: CompanyRequestDataWithFiles): Promise<CompanyResponse> => {
    const { data } = await companiesApi.addCompany(form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const updateCompany = async (
    companyId: string,
    form: CompanyRequestDataWithFiles,
  ): Promise<CompanyResponse> => {
    const { data } = await companiesApi.updateCompany(companyId, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const getCompanyDetails = async (
    companyId: string,
    signal: AbortSignal | undefined,
  ): Promise<CompanyResponse | undefined> => {
    const { data } = await companiesApi.getCompanyById(companyId, { signal });
    return data;
  };

  const showOrganization = async (
    id: string,
    signal: AbortSignal | undefined,
  ): Promise<OrganizationResponse> => {
    const { data } = await organizationsApi.showOrganization(id, { signal });
    return data;
  };

  const updateOrganization = async (
    id: string,
    form: OrganizationUpdateRequestDataWithFiles,
  ): Promise<OrganizationDetailsResponse> => {
    const { data } = await signupRequestsApi.updateOrganization(id, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const updateOrganizationDiscounts = async (
    id: string,
    form: DiscountAndPayment,
  ): Promise<OrganizationDetailsResponse> => {
    const { data } = await organizationsApi.updateOrganizationDiscounts(id, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const fetchRebatePolicies = async (
    request: RebatePoliciesRequest,
    signal: AbortSignal | undefined,
  ): Promise<PageResponseRebatePolicyResponse> => {
    const { data } = await rebatePolicyApi.list(request, { signal });
    return data;
  };

  const showRebatePolicy = async (
    id: string,
    signal: AbortSignal | undefined,
  ): Promise<RebatePolicyResponse> => {
    const { data } = await rebatePolicyApi.show(id, { signal });
    return data;
  };

  const addRebatePolicy = async (form: RebatePolicyRequest): Promise<RebatePolicyResponse> => {
    const { data } = await rebatePolicyApi.add(form);
    return data;
  };

  const updateRebatePolicy = async (
    id: string,
    form: RebatePolicyRequest,
  ): Promise<RebatePolicyResponse> => {
    const { data } = await rebatePolicyApi.update(id, form);
    return data;
  };

  const deleteRebatePolicy = async (id: string): Promise<RebatePolicyResponse> => {
    const { data } = await rebatePolicyApi.deleteRebatePolicy(id);
    return data;
  };

  const downloadOrganizationDocument = useCallback(
    async (organizationId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await organizationsApi.downloadOrganizationDocument(
        organizationId,
        documentId,
        { format: 'blob', signal },
      );
      return data;
    },
    [organizationsApi],
  );

  const updateOrganizationTenants = async (
    organizationId: string,
    form: OrganizationUpdateTenants,
  ): Promise<OrganizationDetailsResponse> => {
    const { data } = await organizationsApi.updateOrganizationTenants(organizationId, form);
    return data;
  };

  const updateOrganizationSettings = async (
    form: OrganizationSettings,
  ): Promise<OrganizationDetailsResponse> => {
    const { data } = await organizationApi.updateOrganizationSettings(form);
    return data;
  };

  const getMyOrganization = async (
    signal?: AbortSignal | undefined,
  ): Promise<OrganizationDetailsResponse> => {
    const { data } = await organizationApi.getMyOrganization({
      signal,
    });
    return data;
  };

  const downloadCompanyDocument = useCallback(
    async (companyId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await companiesApi.downloadCompanyDocument(
        companyId,
        documentId,
        undefined,
        {
          format: 'blob',
          signal,
        },
      );

      return data;
    },
    [companiesApi],
  );

  const updateCompanyDocuments = async (
    companyId: string,
    form: CompanyRequestFiles,
  ): Promise<void> => {
    try {
      await companiesApi.updateCompanyDocuments(companyId, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        type: ContentType.FormData,
      });
      // eslint-disable-next-line no-empty
    } catch (_) {}
  };

  const deleteCompanyDocument = async (
    companyId: string,
    documentId: string,
  ): Promise<CompanyResponse> => {
    const { data } = await companiesApi.deleteCompanyDocument(companyId, documentId);
    return data;
  };

  const updateOrganizationName = async (
    organizationId: string,
    request: OrganizationUpdateName,
  ): Promise<OrganizationResponse> => {
    const { data } = await organizationsApi.updateOrganizationName(organizationId, request);
    return data;
  };

  return {
    getCompanies,
    createCompany,
    updateCompany,
    getCompanyDetails,
    showOrganization,
    updateOrganization,
    fetchRebatePolicies,
    showRebatePolicy,
    addRebatePolicy,
    updateRebatePolicy,
    deleteRebatePolicy,
    downloadOrganizationDocument,
    updateOrganizationTenants,
    updateOrganizationSettings,
    getMyOrganization,
    downloadCompanyDocument,
    updateCompanyDocuments,
    updateOrganizationDiscounts,
    deleteCompanyDocument,
    updateOrganizationName,
  };
};

export default useCompaniesService;
